import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { SealCheck, WarningCircle, Fingerprint, Storefront, ClockCounterClockwise, ShieldCheck, User } from "@phosphor-icons/react";
import { ScoreCircle } from "../components/ScoreCircle";
import { Wordmark } from "../components/Logo";
import { api, formatDate, scoreColor, CATEGORY_LABELS } from "../lib/api";

const CredIcon = ({ weight }) => {
  if (weight === 5) return <ShieldCheck size={16} weight="fill" className="text-score-green" />;
  if (weight === 3) return <SealCheck size={16} weight="fill" className="text-gold" />;
  return <User size={16} weight="light" className="text-navy/40" />;
};

export default function Verify() {
  const { certificateId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/verify/${certificateId}`);
        setData(res.data);
      } catch {
        setData({ valid: false });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificateId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-gold/30 border-t-gold animate-spin" />
      </div>
    );
  }

  if (!data?.valid) {
    return (
      <div className="min-h-screen bg-navy flex flex-col items-center justify-center px-6 text-center">
        <WarningCircle size={56} weight="light" className="text-score-red" />
        <h1 className="text-2xl text-cream mt-4">Certificate Not Found</h1>
        <p className="text-cream/50 text-sm mt-2">This passport could not be verified. It may not exist.</p>
        <p className="text-cream/30 text-xs font-mono mt-4">{certificateId}</p>
      </div>
    );
  }

  const color = scoreColor(data.provenanceScore);

  return (
    <div className="min-h-screen bg-cream">
      {/* header */}
      <div style={{ backgroundColor: data.merchant.brandColor || "#1B2B5E" }}>
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          {data.merchant.logoUrl ? (
            <img src={data.merchant.logoUrl} alt={data.merchant.name} className="h-9 w-auto max-w-[150px] object-contain" data-testid="verify-merchant-logo" />
          ) : (
            <Wordmark light />
          )}
          <span className="flex items-center gap-1.5 text-[0.62rem] uppercase tracking-[0.2em] text-white bg-white/15 rounded-full px-3 py-1.5">
            <SealCheck size={14} weight="fill" /> Verified
          </span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10 sm:py-14">
        {/* hero */}
        <div className="bg-white rounded-3xl border border-navy/10 shadow-luxe p-7 sm:p-10 animate-fade-up">
          <div className="flex flex-col sm:flex-row gap-8 items-center">
            <div className="w-full sm:w-52 shrink-0">
              <div className="aspect-square rounded-2xl bg-cream overflow-hidden">
                {data.product.image ? <img src={data.product.image} alt={data.product.name} className="w-full h-full object-cover" data-testid="verify-product-image" /> : null}
              </div>
            </div>
            <div className="flex-1 text-center sm:text-left">
              <span className="text-[0.62rem] uppercase tracking-[0.2em] text-navy/50 bg-navy/5 rounded-full px-3 py-1">{CATEGORY_LABELS[data.product.category] || data.product.category}</span>
              <h1 className="mt-3 text-3xl sm:text-4xl tracking-tight text-navy leading-tight" data-testid="verify-product-name">{data.product.name}</h1>
              <p className="text-navy/50 mt-1">{data.product.brand}</p>
              {data.product.serialNumber && <p className="mt-3 text-xs text-navy/40 font-mono">Serial · {data.product.serialNumber}</p>}
              {data.warranty?.expiresAt && (
                <div className="mt-3">
                  <span className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5" style={{ background: data.warranty.active ? "rgba(45,106,79,0.10)" : "rgba(27,43,94,0.05)", color: data.warranty.active ? "#2D6A4F" : "rgba(27,43,94,0.55)" }} data-testid="verify-warranty">
                    <ShieldCheck size={13} weight="fill" /> Warranty {data.warranty.active ? "valid until" : "expired"} {formatDate(data.warranty.expiresAt)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* score + meta */}
        <div className="grid sm:grid-cols-3 gap-5 mt-5">
          <div className="bg-white rounded-3xl border border-navy/10 shadow-luxe p-7 flex flex-col items-center justify-center animate-fade-up">
            <ScoreCircle score={data.provenanceScore} size={150} />
            <span className="mt-3 text-xs font-medium" style={{ color }}>
              {data.provenanceScore >= 80 ? "Excellent Provenance" : data.provenanceScore >= 50 ? "Good Provenance" : "Limited Provenance"}
            </span>
          </div>

          <div className="sm:col-span-2 bg-white rounded-3xl border border-navy/10 shadow-luxe p-7 grid grid-cols-2 gap-6 animate-fade-up">
            <Meta icon={Storefront} label="Issued By" value={data.merchant.name} sub={data.merchant.storeDomain} />
            <Meta icon={ClockCounterClockwise} label="Date Issued" value={formatDate(data.issuedAt)} />
            <Meta icon={ShieldCheck} label="Status" value={data.status} valueClass="capitalize text-score-green" />
            <Meta icon={User} label="Transfers" value={`${data.ownership.transferCount} owner${data.ownership.transferCount === 1 ? "" : "s"}`} sub={`Held by ${data.ownership.currentOwnerType}`} />
          </div>
        </div>

        {/* timeline */}
        <div className="bg-white rounded-3xl border border-navy/10 shadow-luxe p-7 sm:p-9 mt-5 animate-fade-up">
          <h2 className="text-xl text-navy mb-6">Provenance Timeline</h2>
          <ol className="relative border-l border-navy/10 ml-2" data-testid="verify-timeline">
            {data.timeline.map((e) => (
              <li key={e.id} className="ml-6 pb-8 last:pb-0">
                <span className="absolute -left-[9px] mt-1 h-4 w-4 rounded-full bg-white border-2" style={{ borderColor: e.weight === 5 ? "#2D6A4F" : e.weight === 3 ? "#C9A84C" : "#94a3b8" }} />
                <div className="flex items-center gap-2 flex-wrap">
                  <CredIcon weight={e.weight} />
                  <span className="text-navy font-medium capitalize">{e.type.replace(/_/g, " ")}</span>
                  <span className="text-[0.6rem] uppercase tracking-wide text-navy/50 bg-cream rounded-full px-2 py-0.5">{e.credibility}</span>
                </div>
                <p className="text-sm text-navy/60 mt-1">{e.description}</p>
                <p className="text-xs text-navy/35 mt-1">{formatDate(e.date)}</p>
              </li>
            ))}
          </ol>
        </div>

        {/* hash */}
        <div className="mt-5 bg-navy rounded-3xl p-7 animate-fade-up">
          <div className="flex items-center gap-2 text-gold mb-2">
            <Fingerprint size={18} weight="light" />
            <span className="text-[0.62rem] uppercase tracking-[0.2em]">Immutable Verification Hash</span>
          </div>
          <p className="text-cream/60 text-xs font-mono break-all leading-relaxed" data-testid="verify-hash">{data.verificationHash}</p>
          <p className="text-cream/30 text-[0.68rem] mt-3 font-mono">{data.certificateId}</p>
        </div>

        <p className="text-center text-navy/40 text-xs mt-10 flex items-center justify-center gap-1.5">
          Powered by <span className="text-navy font-playfair">Vcoin</span> · Valura Technologies
        </p>
      </div>
    </div>
  );
}

const Meta = ({ icon: Icon, label, value, sub, valueClass = "text-navy" }) => (
  <div>
    <div className="flex items-center gap-1.5 text-navy/40 mb-1">
      <Icon size={15} weight="light" />
      <span className="text-[0.6rem] uppercase tracking-[0.18em]">{label}</span>
    </div>
    <p className={`text-sm font-medium ${valueClass}`}>{value}</p>
    {sub && <p className="text-xs text-navy/40 mt-0.5">{sub}</p>}
  </div>
);
